import { memo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  ScrollArea,
} from '@flote/ui';
import { HubForm, type HubFormData } from './HubForm';
import type { Hub, Team } from '@flote/contracts/http';

interface HubFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: HubFormData) => Promise<void>;
  isProcessing?: boolean;
  teams: Team[];
  hub?: Hub;
}

export const HubFormModal = memo<HubFormModalProps>(({
  open,
  onOpenChange,
  onSubmit,
  isProcessing = false,
  teams,
  hub,
}) => {
  const isEditMode = !!hub;

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !isProcessing) {
        onOpenChange(false);
      }
    },
    [onOpenChange, isProcessing]
  );

  const initialData = hub
    ? {
        id: hub.id,
        name: hub.name,
        address: {
          number: hub.address.number,
          street: hub.address.street,
          apartment: hub.address.apartment,
          city: hub.address.city,
          state: hub.address.state,
          postalCode: hub.address.postalCode,
          country: hub.address.country,
        },
        teams: teams.filter(team => team.hub === hub.id).map(team => team.id),
      }
    : undefined;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-2xl max-h-[90vh] p-0 sm:w-full">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>{isEditMode ? 'Edit Hub' : 'Create Hub'}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update hub information and location'
              : 'Create a new hub as a home base for your delivery teams'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-120px)]">
          <div className="px-6 pb-6">
            <HubForm
              onSubmit={onSubmit}
              isProcessing={isProcessing}
              teams={teams}
              initialData={initialData}
              mode={isEditMode ? 'edit' : 'create'}
            />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
});

HubFormModal.displayName = 'HubFormModal';
